import React from "react";
import { useApi } from "@tiny/api";
import { usePutSuppressionBySuppressionIdMutation } from "@tiny/api";
import { useMessages } from "@tiny/components";
import useBreakpoints from "@tiny/components/src/hooks/useBreakpoints";
import { useRefCallBack } from "@tiny/components/src/hooks/useRefCallBack";
import { SuppressionSourceDetailsHeaderProps } from "./SuppressionSourceDetailsHeader";
import { toChipColor } from "../../../components/SuppressionList/SuppressionListRow/SuppressionListRow.helper";

export function useSuppressionSourceDetailsHeader(
  props: SuppressionSourceDetailsHeaderProps
) {
  const api = useApi();
  const { title, suppressionId, suppressionScope } = props;

  const { mdUp } = useBreakpoints();

  const { showWarningMessage, closeMessage, showErrorMessage } = useMessages();

  const [editing, setEditing] = React.useState(false);

  const [internalTitle, setInternalTitle] = React.useState(title);

  const messageIdRef = React.useRef<string | undefined>();

  const {
    mutateAsync: putSuppressionMutateAsync,
    isLoading: isPutSuppressionLoading,
  } = usePutSuppressionBySuppressionIdMutation({
    onError() {
      return undefined;
    },
  });

  const titleEditable = React.useMemo(() => true, []);

  const editTitle = () => {
    setEditing(true);
  };

  const clearMessage = React.useCallback(async () => {
    if (messageIdRef.current) {
      await closeMessage(messageIdRef.current);
      messageIdRef.current = undefined;
    }
  }, [closeMessage]);

  const chipColor = React.useMemo(
    () => toChipColor(suppressionScope),
    [suppressionScope]
  );

  const saveTitle = useRefCallBack(
    React.useCallback(
      async (editingName: string) => {
        clearMessage();
        if (editingName && editingName !== title) {
          try {
            const { data: nameIsUsed } =
              await api.getSuppressionCheckExistsBySuppressionName(editingName);
            if (nameIsUsed.exist) {
              messageIdRef.current = showWarningMessage(
                `Audience with name "${editingName}" already exists!`
              );
            } else {
              await putSuppressionMutateAsync({
                suppressionId: suppressionId,
                data: { name: editingName },
              });
              setInternalTitle(editingName);
            }
          } catch (e) {
            showErrorMessage("Something wrong happened");
          } finally {
            setEditing(false);
          }
        } else {
          setEditing(false);
        }
      },
      [
        clearMessage,
        putSuppressionMutateAsync,
        suppressionId,
        showErrorMessage,
        showWarningMessage,
        title,
      ]
    )
  );

  const cancelEdit = React.useCallback(() => {
    setEditing(false);
    clearMessage();
  }, [clearMessage]);

  React.useEffect(() => {
    setInternalTitle(title);
  }, [title]);

  const saving = isPutSuppressionLoading;

  return {
    editTitle,
    editing,
    cancelEdit,
    internalTitle,
    saveTitle,
    saving,
    mdUp,
    titleEditable,
    chipColor,
  };
}
